import { Fragment } from "react";
import { StatusDot, cx } from "@amber/ui";
import { ITEM_STATUS_LABELS, type ItemStatus, type ItemsResponse } from "../../contracts/index";
import { listOf, toggled } from "../filterList";
import type { BacklogSearch } from "../search";
import { STATUS_TONE } from "./StatusCell";

export function BacklogStrip({
  data,
  search,
  set,
}: {
  data: ItemsResponse;
  search: BacklogSearch;
  set: (patch: Partial<BacklogSearch>) => void;
}) {
  const selected = listOf(search.status);
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 pt-3 pb-1 max-compact:px-2">
      <h1 id="view-title" className="sr-only">
        Backlog
      </h1>
      <ul className="m-0 flex list-none flex-wrap items-center gap-x-5 gap-y-1 p-0" aria-label="Items by status">
        {data.facets.status.map((s) => {
          const status = s.key as ItemStatus;
          const on = selected.includes(status);
          return (
            <li key={s.key}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => {
                  set({ status: toggled(search.status, status) });
                }}
                className={cx(
                  "inline-flex items-baseline gap-1.5 rounded-md px-1 py-0.5 hover:bg-surface-2",
                  on && "bg-accent-soft",
                )}
              >
                <b
                  className={cx(
                    "text-2xl font-semibold",
                    status === "failed" && s.count > 0 && "text-attention",
                    status === "waiting" && s.count > 0 && "text-waiting",
                  )}
                >
                  {s.count}
                </b>
                <span className="inline-flex items-center gap-1.5 text-ui text-muted">
                  <StatusDot tone={STATUS_TONE[status]} />
                  {ITEM_STATUS_LABELS[status]}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <span className="flex-1" />
      <p
        className="m-0 inline-flex items-center gap-2 rounded-full border border-divider px-3 py-1 text-md text-muted max-narrow:hidden"
        aria-live="off"
      >
        <StatusDot tone="running" />
        {data.activity.length === 0 ? (
          "Nothing is processing right now"
        ) : (
          <span>
            Now{" "}
            {data.activity.map((a, i) => (
              <Fragment key={a.stage}>
                {i > 0 ? " · " : ""}
                {a.label} <b className="font-semibold text-fg">{a.count}</b>
              </Fragment>
            ))}
          </span>
        )}
      </p>
    </div>
  );
}
